/* eslint-disable camelcase */
import { useReactiveVar } from '@apollo/client';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { useMenuSelectionsDishes } from 'app/components/BR1258/hooks/useMenuSelectionsDishes';
import { getMaxiBlockDishes } from 'app/components/BR1258/utils/getMaxiBlocksDishes';
import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

const GROUP_SIZE = 3;

const sliceIntoGroups = (dishesList) => {
    const groups = [];
    for (let i = 0; i < dishesList.length; i += GROUP_SIZE) {
        groups.push(
            dishesList.slice(i, i + GROUP_SIZE).map((itm) => `${itm.id}:${itm.categoryId}`),
        );
    }
    return groups;
};

export const usePromoCompoentPosition = (props) => {
    const period = useReactiveVar(selectedPeriodVar);
    const { flatDishesList = [], dishesHashSum } = props;
    const [groups, setGroups] = useState([]);

    const prevHashRef = useRef('');

    const { allDishes } = useMenuSelectionsDishes();
    const maxiBlockDishes = getMaxiBlockDishes();

    const hiddenDishes = useMemo(
        () => [...allDishes, ...maxiBlockDishes],
        [allDishes, maxiBlockDishes],
    );

    const dishesToFilter = useMemo(
        () => hiddenDishes.map((d) => d.id),
        [hiddenDishes],
    );

    const currentDishesToCount = useMemo(
        () => flatDishesList.filter((d) => !dishesToFilter.includes(d.id)),
        [flatDishesList, dishesToFilter],
    );

    useEffect(() => {
        const currentHash = JSON.stringify(currentDishesToCount.map((item) => item.id));

        if (prevHashRef.current === currentHash) {
            return;
        }

        prevHashRef.current = currentHash;

        const newGroups = sliceIntoGroups(currentDishesToCount);

        setGroups((prev) => {
            if (JSON.stringify(prev) === JSON.stringify(newGroups)) {
                return prev;
            }
            return newGroups;
        });
    }, [dishesHashSum, currentDishesToCount]);

    const updatePromoCompoentPosition = useCallback((next_id) => {
        setGroups((prevGroups) => {
            const filteredGroups = prevGroups.map((group) => (
                group.filter((id) => id.split(':')[0] !== next_id)
            ));

            const emptyGroupIndex = filteredGroups.findIndex((group) => group.length === 0);

            if (emptyGroupIndex === -1) {
                return filteredGroups;
            }

            const listed = currentDishesToCount.filter((itm) => itm.id !== next_id);

            const rebuiltGroups = filteredGroups.slice(0, emptyGroupIndex);
            let keptItemsCount = rebuiltGroups.reduce((sum, group) => sum + group.length, 0);

            for (let i = emptyGroupIndex; i < filteredGroups.length; i += 1) {
                const groupItems = listed.slice(keptItemsCount, keptItemsCount + GROUP_SIZE);
                rebuiltGroups.push(groupItems.map((itm) => `${itm.id}:${itm.categoryId}`));
                keptItemsCount += GROUP_SIZE;
            }

            return rebuiltGroups;
        });
    }, [currentDishesToCount]);

    const returnValue = useMemo(() => {
        const [frstThree, secThree, thirdThree, fourthThree, fifthThree] = groups;

        return {
            thirdDishId: frstThree?.[frstThree.length - 1],
            fifthDishId: secThree?.[secThree.length - 2],
            sixthDishId: secThree?.[secThree.length - 1],
            seventhDishId: thirdThree?.[0],
            ninthDishId: thirdThree?.[thirdThree.length - 1],
            tenthDishId: fourthThree?.[0],
            eleventhDishId: fourthThree?.[1],
            twelfthDishId: fourthThree?.[2],
            thirteenthDishId: fifthThree?.[0],
            selectionPositions: groups.slice(1).map((group) => group[group.length - 1]),
            updatePromoCompoentPosition,
        };
    }, [groups, updatePromoCompoentPosition]);

    return returnValue;
};
